import React, { useEffect, useState } from 'react'
import {
	Card,
	Switch,
	Typography,
	Spin,
	message,
	Row,
	Col,
	InputNumber,
	Divider,
	Space,
	Button,
} from 'antd'
import { MobileOutlined, BellOutlined } from '@ant-design/icons'
import { httpGet, httpPut } from '../../common/http.service'

const { Title, Text, Paragraph } = Typography

interface LeaveNotifSettings {
	reminders: {
		enabled: boolean
		hours: number[]
	}
	sla: {
		level1Hours: number
		level2Hours: number
		level3Hours: number
		level4Hours: number
	}
	businessHours: {
		start: number
		end: number
	}
	urgentThresholdHours: number
}

interface AppSettings {
	mobileLeaveManagementEnabled: boolean
	leaveNotif: LeaveNotifSettings
	leaveApproverLevels: {
		level1Required: boolean
		level2Required: boolean
		level3Required: boolean
		adminRequired: boolean
	}
}

type AppSettingsViewMode =
	| 'all'
	| 'mobile-only'
	| 'approval-workflow'
	| 'notifications-sla'

interface AppSettingsTabProps {
	mode?: AppSettingsViewMode
}

const AppSettingsTab: React.FC<AppSettingsTabProps> = ({ mode = 'mobile-only' }) => {
	const showMobileCard = mode === 'all' || mode === 'mobile-only'
	const showNotificationCard = mode === 'all' || mode === 'notifications-sla'
	const showApproverCard = mode === 'all' || mode === 'approval-workflow'
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [settings, setSettings] = useState<AppSettings>({
		mobileLeaveManagementEnabled: true,
		leaveNotif: {
			reminders: { enabled: true, hours: [2, 8, 24] },
			sla: { level1Hours: 8, level2Hours: 8, level3Hours: 8, level4Hours: 24 },
			businessHours: { start: 8, end: 18 },
			urgentThresholdHours: 48,
		},
		leaveApproverLevels: {
			level1Required: true,
			level2Required: true,
			level3Required: false,
			adminRequired: true,
		},
	})

	useEffect(() => {
		loadSettings()
	}, [])

	const loadSettings = async () => {
		try {
			setLoading(true)
			const response: any = await httpGet('/api/v1/admin/app-settings')
			if (response?.data) {
				const leaveNotif = response.data.leaveNotif ?? {}
				setSettings({
					mobileLeaveManagementEnabled:
						response.data.mobileLeaveManagementEnabled ?? true,
					leaveNotif: {
						reminders: {
							enabled: leaveNotif?.reminders?.enabled ?? true,
							hours:
								Array.isArray(leaveNotif?.reminders?.hours) &&
								leaveNotif.reminders.hours.length > 0
									? leaveNotif.reminders.hours
									: [2, 8, 24],
						},
						sla: {
							level1Hours: leaveNotif?.sla?.level1Hours ?? 8,
							level2Hours: leaveNotif?.sla?.level2Hours ?? 8,
							level3Hours:
								leaveNotif?.sla?.level4Hours != null
									? leaveNotif?.sla?.level3Hours ?? 8
									: 8,
							level4Hours: leaveNotif?.sla?.level4Hours ?? leaveNotif?.sla?.level3Hours ?? 24,
						},
						businessHours: {
							start: leaveNotif?.businessHours?.start ?? 8,
							end: leaveNotif?.businessHours?.end ?? 18,
						},
						urgentThresholdHours: leaveNotif?.urgentThresholdHours ?? 48,
					},
					leaveApproverLevels: {
						level1Required: response.data?.leaveApproverLevels?.level1Required ?? true,
						level2Required: response.data?.leaveApproverLevels?.level2Required ?? true,
						level3Required: response.data?.leaveApproverLevels?.level3Required ?? false,
						adminRequired: response.data?.leaveApproverLevels?.adminRequired ?? true,
					},
				})
			}
		} catch (error) {
			console.error('Failed to load app settings:', error)
		} finally {
			setLoading(false)
		}
	}

	const handleToggle = async (field: keyof AppSettings, value: boolean) => {
		const previous = settings
		const updated = { ...settings, [field]: value }
		setSettings(updated)
		try {
			setSaving(true)
			const response: any = await httpGet('/api/v1/admin/app-settings')
			const current = response?.data ?? {}
			await httpPut('/api/v1/admin/app-settings', { ...current, ...updated })
			message.success('Mobile leave management updated')
		} catch (error: any) {
			message.error(error?.response?.data?.message || 'Failed to update setting')
			setSettings(previous)
		} finally {
			setSaving(false)
		}
	}

	const setLeaveNotif = (updater: (prev: LeaveNotifSettings) => LeaveNotifSettings) => {
		setSettings((prev) => ({
			...prev,
			leaveNotif: updater(prev.leaveNotif),
		}))
	}

	const getApproverFlowPreview = () => {
		const steps: string[] = []
		if (settings.leaveApproverLevels.level1Required) steps.push('L1')
		if (settings.leaveApproverLevels.level2Required) steps.push('L2')
		if (settings.leaveApproverLevels.level3Required) steps.push('L3')
		if (settings.leaveApproverLevels.adminRequired) steps.push('Admin')
		return steps.join(' -> ')
	}

	const handleSaveAll = async () => {
		const reminderHours = settings.leaveNotif.reminders.hours
			.map((n) => Number(n))
			.filter((n) => Number.isFinite(n) && n > 0)
		if (reminderHours.length === 0) {
			message.error('Please provide at least one valid reminder hour')
			return
		}
		if (settings.leaveNotif.businessHours.start >= settings.leaveNotif.businessHours.end) {
			message.error('Business hour start must be less than end')
			return
		}
		if (
			!settings.leaveApproverLevels.level1Required &&
			!settings.leaveApproverLevels.level2Required &&
			!settings.leaveApproverLevels.level3Required &&
			!settings.leaveApproverLevels.adminRequired
		) {
			message.error('At least one approver level must be required')
			return
		}
		try {
			setSaving(true)
			const response: any = await httpGet('/api/v1/admin/app-settings')
			const current = response?.data ?? {}
			await httpPut('/api/v1/admin/app-settings', {
				...current,
				...settings,
				leaveNotif: {
					...settings.leaveNotif,
					reminders: {
						...settings.leaveNotif.reminders,
						hours: reminderHours,
					},
				},
			})
			message.success('App settings updated')
		} catch (error: any) {
			message.error(error?.response?.data?.message || 'Failed to update settings')
		} finally {
			setSaving(false)
		}
	}

	return (
		<div>
			<Spin spinning={loading}>
				{showMobileCard ? (
					<Card
					title={
						<span>
							<MobileOutlined style={{ marginRight: 8 }} />
							Mobile Leave Management
						</span>
					}
					bordered={false}
					style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
				>
					<Paragraph type='secondary' style={{ marginBottom: 24 }}>
						Control whether the Leaves feature is available on the mobile app. Changes
						take effect immediately for all users.
					</Paragraph>

					<Row align='middle' justify='space-between' style={{ padding: '12px 0' }}>
						<Col flex='1'>
							<Title level={5} style={{ margin: 0 }}>
								Enable Mobile Leave Management
							</Title>
							<Text type='secondary'>
								When disabled, employees cannot view, request, or approve leaves
								from the mobile app.
							</Text>
						</Col>
						<Col>
							<Switch
								checked={settings.mobileLeaveManagementEnabled}
								onChange={(val) =>
									handleToggle('mobileLeaveManagementEnabled', val)
								}
								loading={saving}
								checkedChildren='Enabled'
								unCheckedChildren='Disabled'
							/>
						</Col>
					</Row>
					<Divider />
					<Space style={{ width: '100%', justifyContent: 'flex-end' }}>
						<Button type='primary' onClick={handleSaveAll} loading={saving}>
							Save App Settings
						</Button>
					</Space>
					</Card>
				) : null}

				{showNotificationCard ? (
					<Card
					title={
						<span>
							<BellOutlined style={{ marginRight: 8 }} />
							Leave Notification Configuration
						</span>
					}
					bordered={false}
					style={{
						marginTop: 16,
						boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
					}}
				>
					<Paragraph type='secondary' style={{ marginBottom: 20 }}>
						Control reminders, SLA escalation, business hours, and urgency threshold
						for leave approval notifications.
					</Paragraph>

					<Row align='middle' justify='space-between' style={{ padding: '12px 0' }}>
						<Col flex='1'>
							<Title level={5} style={{ margin: 0 }}>
								Enable Reminder Notifications
							</Title>
							<Text type='secondary'>
								When disabled, no scheduled reminder notifications are sent.
							</Text>
						</Col>
						<Col>
							<Switch
								checked={settings.leaveNotif.reminders.enabled}
								onChange={(enabled) =>
									setLeaveNotif((prev) => ({
										...prev,
										reminders: { ...prev.reminders, enabled },
									}))
								}
							/>
						</Col>
					</Row>

					<Divider />

					<Row gutter={[16, 16]}>
						<Col xs={24} md={8}>
							<Text>Reminder Hour 1</Text>
							<InputNumber
								min={1}
								max={168}
								style={{ width: '100%' }}
								value={settings.leaveNotif.reminders.hours[0]}
								onChange={(v) =>
									setLeaveNotif((prev) => ({
										...prev,
										reminders: {
											...prev.reminders,
											hours: [Number(v || 0), prev.reminders.hours[1], prev.reminders.hours[2]],
										},
									}))
								}
							/>
						</Col>
						<Col xs={24} md={8}>
							<Text>Reminder Hour 2</Text>
							<InputNumber
								min={1}
								max={168}
								style={{ width: '100%' }}
								value={settings.leaveNotif.reminders.hours[1]}
								onChange={(v) =>
									setLeaveNotif((prev) => ({
										...prev,
										reminders: {
											...prev.reminders,
											hours: [prev.reminders.hours[0], Number(v || 0), prev.reminders.hours[2]],
										},
									}))
								}
							/>
						</Col>
						<Col xs={24} md={8}>
							<Text>Reminder Hour 3</Text>
							<InputNumber
								min={1}
								max={168}
								style={{ width: '100%' }}
								value={settings.leaveNotif.reminders.hours[2]}
								onChange={(v) =>
									setLeaveNotif((prev) => ({
										...prev,
										reminders: {
											...prev.reminders,
											hours: [prev.reminders.hours[0], prev.reminders.hours[1], Number(v || 0)],
										},
									}))
								}
							/>
						</Col>
					</Row>

					<Divider />

					<Row gutter={[16, 16]}>
						<Col xs={24} md={8}>
							<Text>SLA Level 1 (hours)</Text>
							<InputNumber
								min={1}
								max={240}
								style={{ width: '100%' }}
								value={settings.leaveNotif.sla.level1Hours}
								onChange={(v) =>
									setLeaveNotif((prev) => ({
										...prev,
										sla: { ...prev.sla, level1Hours: Number(v || 1) },
									}))
								}
							/>
						</Col>
						<Col xs={24} md={8}>
							<Text>SLA Level 2 (hours)</Text>
							<InputNumber
								min={1}
								max={240}
								style={{ width: '100%' }}
								value={settings.leaveNotif.sla.level2Hours}
								onChange={(v) =>
									setLeaveNotif((prev) => ({
										...prev,
										sla: { ...prev.sla, level2Hours: Number(v || 1) },
									}))
								}
							/>
						</Col>
						<Col xs={24} md={8}>
							<Text>SLA Level 3 — 3rd line manager (hours)</Text>
							<InputNumber
								min={1}
								max={240}
								style={{ width: '100%' }}
								value={settings.leaveNotif.sla.level3Hours}
								onChange={(v) =>
									setLeaveNotif((prev) => ({
										...prev,
										sla: { ...prev.sla, level3Hours: Number(v || 1) },
									}))
								}
							/>
						</Col>
						<Col xs={24} md={8}>
							<Text>SLA Level 4 — Admin (hours)</Text>
							<InputNumber
								min={1}
								max={240}
								style={{ width: '100%' }}
								value={settings.leaveNotif.sla.level4Hours}
								onChange={(v) =>
									setLeaveNotif((prev) => ({
										...prev,
										sla: { ...prev.sla, level4Hours: Number(v || 1) },
									}))
								}
							/>
						</Col>
					</Row>

					<Divider />

					<Row gutter={[16, 16]}>
						<Col xs={24} md={8}>
							<Text>Business Start Hour</Text>
							<InputNumber
								min={0}
								max={23}
								style={{ width: '100%' }}
								value={settings.leaveNotif.businessHours.start}
								onChange={(v) =>
									setLeaveNotif((prev) => ({
										...prev,
										businessHours: { ...prev.businessHours, start: Number(v || 0) },
									}))
								}
							/>
						</Col>
						<Col xs={24} md={8}>
							<Text>Business End Hour</Text>
							<InputNumber
								min={1}
								max={24}
								style={{ width: '100%' }}
								value={settings.leaveNotif.businessHours.end}
								onChange={(v) =>
									setLeaveNotif((prev) => ({
										...prev,
										businessHours: { ...prev.businessHours, end: Number(v || 1) },
									}))
								}
							/>
						</Col>
						<Col xs={24} md={8}>
							<Text>Urgent Threshold (hours)</Text>
							<InputNumber
								min={1}
								max={168}
								style={{ width: '100%' }}
								value={settings.leaveNotif.urgentThresholdHours}
								onChange={(v) =>
									setLeaveNotif((prev) => ({
										...prev,
										urgentThresholdHours: Number(v || 1),
									}))
								}
							/>
						</Col>
					</Row>

					<Divider />
					<Space style={{ width: '100%', justifyContent: 'flex-end' }}>
						<Button type='primary' onClick={handleSaveAll} loading={saving}>
							Save Notification Settings
						</Button>
					</Space>
					</Card>
				) : null}

				{showApproverCard ? (
					<Card
					title='Manage Level Approver'
					bordered={false}
					style={{
						marginTop: showNotificationCard || showMobileCard ? 16 : 0,
						boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
					}}
				>
					<Paragraph type='secondary' style={{ marginBottom: 20 }}>
						Choose which approval levels are required in the leave workflow.
					</Paragraph>
					<Paragraph type='secondary' style={{ marginTop: -8 }}>
						Examples: <Text code>L1 + L2 + L3 + Admin</Text> = full chain,{' '}
						<Text code>L1 + Admin</Text> = skip L2/L3, <Text code>Admin only</Text> =
						direct final admin approval.
					</Paragraph>
					<Paragraph style={{ marginBottom: 16 }}>
						<Text strong>Current flow: </Text>
						<Text code>{getApproverFlowPreview()}</Text>
					</Paragraph>
					<Row align='middle' justify='space-between' style={{ padding: '12px 0' }}>
						<Col flex='1'>
							<Title level={5} style={{ margin: 0 }}>
								Level 1 Manager Required
							</Title>
							<Text type='secondary'>
								First line manager must approve before moving to next required level.
							</Text>
						</Col>
						<Col>
							<Switch
								checked={settings.leaveApproverLevels.level1Required}
								onChange={(checked) =>
									setSettings((prev) => ({
										...prev,
										leaveApproverLevels: {
											...prev.leaveApproverLevels,
											level1Required: checked,
										},
									}))
								}
							/>
						</Col>
					</Row>
					<Row align='middle' justify='space-between' style={{ padding: '12px 0' }}>
						<Col flex='1'>
							<Title level={5} style={{ margin: 0 }}>
								Level 2 Manager Required
							</Title>
							<Text type='secondary'>
								Second line manager approval is included in the sequence when enabled.
							</Text>
						</Col>
						<Col>
							<Switch
								checked={settings.leaveApproverLevels.level2Required}
								onChange={(checked) =>
									setSettings((prev) => ({
										...prev,
										leaveApproverLevels: {
											...prev.leaveApproverLevels,
											level2Required: checked,
										},
									}))
								}
							/>
						</Col>
					</Row>
					<Row align='middle' justify='space-between' style={{ padding: '12px 0' }}>
						<Col flex='1'>
							<Title level={5} style={{ margin: 0 }}>
								Level 3 Manager Required
							</Title>
							<Text type='secondary'>
								Third line manager step is included when enabled (requires staff{' '}
								<Text code>Third Line Manager</Text> to be set).
							</Text>
						</Col>
						<Col>
							<Switch
								checked={settings.leaveApproverLevels.level3Required}
								onChange={(checked) =>
									setSettings((prev) => ({
										...prev,
										leaveApproverLevels: {
											...prev.leaveApproverLevels,
											level3Required: checked,
										},
									}))
								}
							/>
						</Col>
					</Row>
					<Row align='middle' justify='space-between' style={{ padding: '12px 0' }}>
						<Col flex='1'>
							<Title level={5} style={{ margin: 0 }}>
								Admin Final Approval Required
							</Title>
							<Text type='secondary'>
								Admin acts as final approver when this level is enabled.
							</Text>
						</Col>
						<Col>
							<Switch
								checked={settings.leaveApproverLevels.adminRequired}
								onChange={(checked) =>
									setSettings((prev) => ({
										...prev,
										leaveApproverLevels: {
											...prev.leaveApproverLevels,
											adminRequired: checked,
										},
									}))
								}
							/>
						</Col>
					</Row>

					<Divider />
					<Space style={{ width: '100%', justifyContent: 'flex-end' }}>
						<Button type='primary' onClick={handleSaveAll} loading={saving}>
							Save Approver Levels
						</Button>
					</Space>
					</Card>
				) : null}
			</Spin>
		</div>
	)
}

export default AppSettingsTab
